import type { Role } from "./roles";

const opts: RequestInit = { credentials: "include", headers: { "Content-Type": "application/json" } };

async function parse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `Request failed (${res.status})`);
  return body as T;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
}

export interface AuthSession {
  configured: boolean;
  has_users: boolean;
  email: boolean;
  email_from: string;
  me: SessionUser | null;
}

export interface TeamMemberDto {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: "active" | "disabled" | "invited";
  last_active: string | null;
  joined_at: string;
  focus: string | null;
}

export interface TeamActivityDto {
  id: string;
  at: string;
  actor: string;
  text: string;
}

export async function fetchSession(): Promise<AuthSession> {
  const res = await fetch("/api/v1/auth/session", { credentials: "include" });
  if (!res.ok) {
    return { configured: false, has_users: false, email: false, email_from: "newsletter@altibbi.me", me: null };
  }
  return res.json();
}

export async function login(email: string, password: string): Promise<SessionUser> {
  const body = await parse<{ data: SessionUser }>(await fetch("/api/v1/auth/login", {
    ...opts, method: "POST", body: JSON.stringify({ email, password }),
  }));
  return body.data;
}

export async function bootstrap(input: { name: string; email: string; password: string }): Promise<SessionUser> {
  const body = await parse<{ data: SessionUser }>(await fetch("/api/v1/auth/bootstrap", {
    ...opts, method: "POST", body: JSON.stringify(input),
  }));
  return body.data;
}

export async function logout(): Promise<void> {
  await fetch("/api/v1/auth/logout", { ...opts, method: "POST" });
}

export async function fetchInvite(
  token: string,
): Promise<{ email: string; name: string; role: Role; returning?: boolean }> {
  const body = await parse<{ data: { email: string; name: string; role: Role; returning?: boolean } }>(
    await fetch(`/api/v1/auth/invite/${token}`, { credentials: "include" }),
  );
  return body.data;
}

/**
 * Ask for a reset link. The answer is the same whether or not the address has
 * an account, so this never becomes a way of asking who works here — there is
 * nothing in the response worth branching on.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await parse<{ data: { sent: boolean } }>(await fetch("/api/v1/auth/forgot", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  }));
}

export async function acceptInvite(token: string, password: string): Promise<SessionUser> {
  const body = await parse<{ data: SessionUser }>(await fetch("/api/v1/auth/accept", {
    ...opts, method: "POST", body: JSON.stringify({ token, password }),
  }));
  return body.data;
}

export async function fetchTeam(): Promise<{ members: TeamMemberDto[]; activity: TeamActivityDto[] }> {
  const body = await parse<{ data: { members: TeamMemberDto[]; activity: TeamActivityDto[] } }>(
    await fetch("/api/v1/team", { credentials: "include" }),
  );
  return body.data;
}

export async function invitePerson(input: { name: string; email: string; role: Role; focus?: string }): Promise<void> {
  await parse(await fetch("/api/v1/team/invite", {
    ...opts, method: "POST", body: JSON.stringify(input),
  }));
}

export async function resendInvite(id: string): Promise<void> {
  await parse(await fetch(`/api/v1/team/${id}/resend`, { ...opts, method: "POST" }));
}

export async function setMemberPassword(id: string, password: string): Promise<void> {
  await parse(await fetch(`/api/v1/team/${id}/password`, {
    ...opts, method: "POST", body: JSON.stringify({ password }),
  }));
}

export async function setMemberRole(id: string, role: Role): Promise<void> {
  await parse(await fetch(`/api/v1/team/${id}`, {
    ...opts, method: "PATCH", body: JSON.stringify({ role }),
  }));
}

export async function removePerson(id: string): Promise<void> {
  await parse(await fetch(`/api/v1/team/${id}`, { ...opts, method: "DELETE" }));
}
